import authInfoSource, { AuthInfoSource } from 'utils/AuthInfoSource';
import { Account, Actor, AccessToken, RoleNames } from './Account';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  AccountWebService,
  RestfulAccountWebService
} from 'ws/AccountWebService';
import i18n from 'i18n';
import {
  SessionStorageHelper,
  SessionStorageItemKeys
} from 'helper/StorageHelper';
import { isRoles } from 'core/permission/PermissionDSL';
import { uniq } from 'lodash';
import { SiteAnnouncement } from 'core/siteAnnouncement/SiteAnnouncement';

const SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY = 'siteAnnouncementReadLogs';

export interface AuthenticationManager {
  readonly logined: boolean;
  readonly actor: Actor | null;
  readonly account: Account | null;
  readonly event: UpdateEventListener<AuthenticationManager>;

  /**
   * Log out the current session.
   */
  logout(): void;

  /**
   * Switch to the specified actor as the current actor.
   *
   * @param actorId actor ID
   * @returns the actor promise
   */
  switchActor(actorId: number): void;

  /**
   * Fetch the account information from the server.
   *
   * @returns the account promise
   */
  fetchAccount(): Promise<Account>;

  /**
   * Log in with the given email and password.
   *
   * @param email the user email
   * @param password the user password
   * @returns the account promise
   */
  login(email: string, password: string): Promise<Account>;

  /**
   *
   * @param accountId the user accountId
   */
  sudo(accountId: number);

  validateGuestToken(token: string): Promise<{
    userName: string;
    registerStatus: number;
  }>;

  setUpPasswordFirstTime(token: string, password: string): Promise<void>;

  sendResetPasswordMail(email: string): Promise<void>;

  hasReadAnnouncement(announcement: SiteAnnouncement): boolean;

  markAnnouncementAsRead(announcement: SiteAnnouncement): void;
}

function findActor(account: Account, actorId: number): Actor | undefined {
  return account.actors.find((actor) => actor.id === actorId);
}

export class DefaultAuthenticationManager implements AuthenticationManager {
  source: AuthInfoSource;
  currentActor: Actor | null;
  loginedAccount: Account | null;
  accountWebService: AccountWebService;
  authEvent: FireableUpdateEventListener<AuthenticationManager>;

  constructor(
    source: AuthInfoSource = authInfoSource,
    accountWebService: AccountWebService = new RestfulAccountWebService()
  ) {
    this.source = source;
    this.currentActor = null;
    this.loginedAccount = null;
    this.accountWebService = accountWebService;
    this.authEvent = new FireableUpdateEventListener<AuthenticationManager>();
  }

  get logined(): boolean {
    const token = this.source.getToken();
    return token.length > 0;
  }

  get event(): UpdateEventListener<AuthenticationManager> {
    return this.authEvent;
  }

  get actor(): Actor | null {
    return this.currentActor;
  }

  get account(): Account | null {
    return this.loginedAccount;
  }

  hasReadAnnouncement(announcement: SiteAnnouncement): boolean {
    if (!this.account || !this.actor) {
      return false;
    }

    if (this.actor.roleName === 'ADMIN') {
      return true;
    }

    const readLogs = this.getAnnouncementReadLogs(
      SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY
    );
    const userKey = `${this.account.id}`;
    const readLogsOfUser = Array.isArray(readLogs[userKey])
      ? readLogs[userKey]
      : [];
    const announcementKey = `${announcement.id}_${announcement.version}`;
    return readLogsOfUser.includes(announcementKey);
  }

  markAnnouncementAsRead(announcement: SiteAnnouncement): void {
    if (!this.account || !this.actor) {
      return;
    }

    const readLogs = this.getAnnouncementReadLogs(
      SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY
    );
    const userKey = `${this.account.id}`;
    const announcementKeyPrefix = `${announcement.id}_`;
    // remove all previous versions of the announcement
    const readLogsOfUser = Array.isArray(readLogs[userKey])
      ? readLogs[userKey]
      : [];
    readLogs[userKey] = readLogsOfUser.filter(
      (key: string) => !key.startsWith(announcementKeyPrefix)
    );
    // add the current version of the announcement
    const announcementKey = `${announcement.id}_${announcement.version}`;
    readLogs[userKey] = uniq([...readLogs[userKey], announcementKey]);
    localStorage.setItem(
      SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY,
      JSON.stringify(readLogs)
    );
  }

  getAnnouncementReadLogs(storageKey: string): Record<string, string[]> {
    const announcementsReadLogs = localStorage.getItem(storageKey) || '{}';
    try {
      return JSON.parse(announcementsReadLogs);
    } catch (e) {
      // If parsing fails, we return an empty object
      return {};
    }
  }

  logout() {
    this.source.clear();
    this.currentActor = null;
    this.loginedAccount = null;
    this.notifyListeners();
  }

  async fetchAccount(): Promise<Account> {
    const account = await this.accountWebService.getAccount();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  switchActor(actorId: number) {
    if (this.currentActor !== null && this.currentActor.id === actorId) {
      return;
    }
    const target = this.switchableActor(actorId);
    this.updateActor(target);
    this.notifyListeners();
  }

  async login(email: string, password: string): Promise<Account> {
    const account = await this.accountWebService.login(email, password);
    this.source.clear();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  async sudo(accountId: number): Promise<Account> {
    const account = await this.accountWebService.sudo(accountId);
    this.source.clear();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  async validateGuestToken(token: string): Promise<{
    userName: string;
    registerStatus: number;
  }> {
    return this.accountWebService.validateGuestToken(token);
  }

  async setUpPasswordFirstTime(token: string, password: string): Promise<void> {
    return this.accountWebService.setUpPasswordFirstTime(token, password);
  }

  async sendResetPasswordMail(email: string): Promise<void> {
    return this.accountWebService.sendResetPasswordMail(email);
  }

  updateAccount(account: Account) {
    this.loginedAccount = account;
    const actorId = this.source.getActor();
    const actor =
      actorId !== null ? findActor(account, actorId) : account.actors[0];
    if (actor) {
      this.updateActor(actor);
    } else {
      throw new Error(i18n.t('authenticationManager.errors.noActor'));
    }
  }

  updateActor(actor: Actor) {
    // if currentActor is exist, need clear agency and advertiser cache
    if (this.currentActor) {
      SessionStorageHelper.removeItem(SessionStorageItemKeys.AGENCY);
      SessionStorageHelper.removeItem(SessionStorageItemKeys.ADVERTISER);
    }
    this.currentActor = actor;
    this.source.setActor(actor.id);
    if (
      actor &&
      isRoles(
        RoleNames.adsAdmin,
        RoleNames.adsSales,
        RoleNames.adsReport
      ).visible({ actor }) &&
      actor.advertiserId
    ) {
      SessionStorageHelper.setItem(
        SessionStorageItemKeys.ADVERTISER,
        actor.advertiserId
      );
    }
    if (
      actor &&
      isRoles(
        RoleNames.agencyAdmin,
        RoleNames.agencyManager,
        RoleNames.agencyOperator,
        RoleNames.agencyReport,
        RoleNames.agencySales,
        RoleNames.beaconExecutive
      ).visible({ actor }) &&
      actor.agencyId
    ) {
      SessionStorageHelper.setItem(
        SessionStorageItemKeys.AGENCY,
        actor.agencyId
      );
    }
    this.updateAccessToken(actor.accessToken);
  }

  updateAccessToken(accessToken: AccessToken) {
    const token = accessToken.token;
    const expires = accessToken.expires;
    this.source.setToken(token, expires);
  }

  switchableActor(actorId: number): Actor {
    if (this.loginedAccount === null) {
      throw new Error('not logined yet');
    }
    const target = findActor(this.loginedAccount, actorId);
    if (target) {
      return target;
    } else {
      throw new Error('illegal actor ID');
    }
  }

  notifyListeners() {
    this.authEvent.fireEvent(this);
  }
}
